import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Globe } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const shouldShowDashboardTreatment = (pathname = "") => {
  const supportedRoutes = new Set([
    "/assessment-results",
    "/compare-countries",
    "/plan",
    "/payment-success",
    "/methodology",
    "/contact",
    "/refund-policy",
    "/terms",
    "/privacy",
  ]);

  return /^\/country\/[a-z]{2}$/i.test(pathname) || supportedRoutes.has(pathname);
};

const shouldShowInsightsPublicNav = (pathname = "") => {
  const insightRoutes = new Set([
    "/about",
    "/insights",
  ]);

  return insightRoutes.has(pathname) || pathname.startsWith("/insights/");
};

const Header = ({ children, sticky = true }) => {
  const location = useLocation();
  const showDashboardTreatment = shouldShowDashboardTreatment(location.pathname);
  const showInsightsPublicNav = shouldShowInsightsPublicNav(location.pathname);
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const checkAuthStatus = async () => {
      try {
        const response = await axios.get(`${API}/auth/status`, { withCredentials: true });
        if (isMounted) {
          setIsAuthenticated(Boolean(response.data?.authenticated));
        }
      } catch {
        if (isMounted) {
          setIsAuthenticated(false);
        }
      }
    };

    checkAuthStatus();
    return () => {
      isMounted = false;
    };
  }, []);

  if (showInsightsPublicNav) {
    return (
      <header className={`border-b border-stone-800 bg-[rgba(12,10,9,0.96)] text-stone-50 shadow-[0_10px_30px_rgba(0,0,0,0.16)] backdrop-blur-md ${sticky ? 'sticky top-0 z-50' : ''}`}>
        <div className="rc-container">
          <div className="flex min-h-16 flex-wrap items-center justify-between gap-3 py-3 sm:h-16 sm:flex-nowrap sm:py-0">
            <Link to="/" className="flex items-center gap-3 group" data-testid="public-insights-header-home-link">
              <div className="flex flex-wrap items-center justify-end gap-2 sm:flex-nowrap">
                <Globe className="w-7 h-7 text-amber-400" />
                <span className="font-manrope font-semibold text-xl tracking-tight text-stone-50">Relocation Clarity</span>
              </div>
              <span className="hidden md:inline text-sm font-normal text-stone-300/90">
                Independent Relocation Guidance
              </span>
            </Link>
            <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
              <Link to="/about" className="px-1 text-sm text-stone-300 transition-colors hover:text-stone-50" data-testid="public-insights-header-nav-about">
                About
              </Link>
              <Link to="/insights" className="px-1 text-sm text-stone-100 transition-colors hover:text-amber-300" data-testid="public-insights-header-nav-insights">
                Insights
              </Link>
              <Link to="/methodology" className="px-1 text-sm text-stone-300 transition-colors hover:text-stone-50" data-testid="public-insights-header-nav-faq">
                FAQ
              </Link>
              <Link to="/contact" className="mr-4 px-1 text-sm text-stone-300 transition-colors hover:text-stone-50 sm:mr-6" data-testid="public-insights-header-nav-contact">
                Contact
              </Link>
              <Link
                to="/auth"
                className="inline-flex h-10 items-center rounded-full bg-stone-50 px-5 text-sm font-medium text-stone-900 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-amber-100"
                data-testid="public-insights-header-assessment-button"
              >
                Take the Assessment
              </Link>
            </div>
          </div>
          <div className="md:hidden pb-2 -mt-1">
            <span className="text-xs text-stone-300/90 leading-tight">
              Independent Relocation Guidance
            </span>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className={`border-b border-stone-800 bg-[rgba(12,10,9,0.96)] text-stone-50 shadow-[0_10px_30px_rgba(0,0,0,0.16)] backdrop-blur-md ${sticky ? 'sticky top-0 z-50' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex min-h-16 flex-wrap items-center justify-between gap-3 py-3 sm:h-16 sm:flex-nowrap sm:py-0">
          <Link to="/" className="flex items-center gap-3 group" data-testid="global-header-home-link">
            <div className="flex flex-wrap items-center justify-end gap-2 sm:flex-nowrap">
              <Globe className="w-7 h-7 text-amber-400" />
              <span className="font-manrope font-semibold text-xl tracking-tight text-stone-50">Relocation Clarity</span>
            </div>
            <span className="hidden md:inline text-sm font-normal text-stone-300/90">
              Independent Relocation Guidance
            </span>
          </Link>
          {(showDashboardTreatment || children || isAuthenticated === false) ? (
            <div className="flex items-center gap-2">
              {showDashboardTreatment ? (
                <Link
                  to="/dashboard"
                  data-testid="global-header-dashboard-link"
                  className="inline-flex items-center gap-2 rounded-full border border-stone-700 bg-stone-900/80 px-4 py-2 text-sm text-stone-300 transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-amber-400/45 hover:bg-stone-900 hover:text-stone-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Dashboard</span>
                </Link>
              ) : null}
              {isAuthenticated === false ? (
                <Link
                  to="/auth?returning=true"
                  data-testid="global-header-login-link"
                  className="inline-flex items-center gap-2 rounded-full border border-stone-700 bg-stone-900/80 px-4 py-2 text-sm text-stone-300 transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-amber-400/45 hover:bg-stone-900 hover:text-stone-50"
                >
                  <span>Returning user? Log in</span>
                </Link>
              ) : null}
              {children}
            </div>
          ) : null}
        </div>
        <div className="md:hidden pb-2 -mt-1">
          <span className="text-xs text-stone-300/90 leading-tight">
            Independent Relocation Guidance
          </span>
        </div>
      </div>
    </header>
  );
};

export { Header };
export default Header;
